import React from 'react';
import ReactDOM from 'react-dom';

import Stack from '@mui/material/Stack';
import Snackbar, {
  SnackbarCloseReason,
  SnackbarOrigin,
  SnackbarProps
} from '@mui/material/Snackbar';
import Slide, { SlideProps } from '@mui/material/Slide';
import { styled } from '@mui/material/styles';

import { ReactComponent as CheckCircleFillIcon } from '../../../static/svg/check-circle-fill.svg';
import { ReactComponent as ErrorFillIcon } from '../../../static/svg/x-circle-fill.svg';
import { ReactComponent as WarningFillIcon } from '../../../static/svg/exclamation-triangle-fill.svg';
import { DEFAULT_NOTIFICATION_DURATION } from '../../../utils/constants';

export type SnackbarAlert = 'success' | 'error' | 'warning';

type SnackbarEl = {
  variant: SnackbarAlert;
  iconEl: React.ReactElement;
  background: string;
};

const TOAST_VARIANTS: Array<SnackbarEl> = [
  {
    variant: 'success',
    iconEl: <CheckCircleFillIcon color="#2d832d" />,
    background: '#edf7ed'
  },
  {
    variant: 'error',
    iconEl: <ErrorFillIcon color="#f87171" />,
    background: '#fee2e2'
  },
  {
    variant: 'warning',
    iconEl: <WarningFillIcon color="#fb923c" />,
    background: '#ffedd5'
  }
];

const Toast = styled(Stack)<{ background: string }>(
  ({ theme, background }) => ({
    background: background,
    position: 'relative',
    marginBottom: '8px',
    padding: '8px 16px',
    borderRadius: '8px',
    '& > svg': {
      marginRight: 10
    },
    ...theme.typography.body2,
    fontSize: '.75rem'
  })
);

// export const NotificationContainer = styled('div')(({ theme }) => ({
//   position: 'fixed',
//   top: 20,
//   maxWidth: 700,
//   left: '50%',
//   transform: 'translateX(-50%)',
//   zIndex: theme.zIndex.modal + 1100
// }));

export type MHSnackbarProps = SnackbarProps & {
  headline?: string;
  variant: SnackbarAlert | undefined;
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const MHSnackbar = (props: MHSnackbarProps) => {
  const toast = TOAST_VARIANTS.find((t) => t.variant === props.variant);

  if (!toast) {
    return null;
  }

  const handleClose = (
    event: React.SyntheticEvent<any> | Event,
    reason: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    props.onClose && props.onClose(event, reason);
  };

  return (
    <Snackbar
      open={props.open}
      autoHideDuration={DEFAULT_NOTIFICATION_DURATION}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1100
      }}>
      <Toast
        direction="row"
        spacing={2}
        alignItems="center"
        background={(toast as SnackbarEl).background}>
        {(toast as SnackbarEl).iconEl}
        {props.message}
      </Toast>
    </Snackbar>
  );
};

export default MHSnackbar;
